/** NX-415 — wysyłka dokumentów do klienta (karta klienta, Windykacja, Ustawienia). */
export const cdocPl = {
  send: 'Wyślij klientowi',
  title: 'Wyślij dokumenty klientowi',
  description: 'Zestawienia informacyjne NEXOPS — faktury i WZ wysyła Subiekt (KSeF).',
  kinds: {
    DOCUMENT_LIST: 'Lista dokumentów za okres',
    RECEIVABLES_STATEMENT: 'Zestawienie należności',
    BALANCE_CONFIRMATION: 'Potwierdzenie salda',
    PAYMENT_REMINDER: 'Przypomnienie o płatności',
    ORDER_CONFIRMATION: 'Potwierdzenie zamówienia',
  },
  kindHints: {
    DOCUMENT_LIST: 'Faktury, korekty i inne dokumenty klienta z wybranego okresu.',
    RECEIVABLES_STATEMENT: 'Nierozliczone faktury z terminami, dniami po terminie i sumą.',
    BALANCE_CONFIRMATION: 'Formularz do potwierdzenia salda lub zgłoszenia rozbieżności.',
    PAYMENT_REMINDER: 'Pismo z listą faktur po terminie; zapisuje się w Windykacji jako e-monit.',
  },
  kind: 'Dokument',
  period: 'Okres',
  from: 'Od',
  to: 'Do',
  asOf: 'Stan należności na {date}',
  asOfHint: 'Zestawienie, potwierdzenie i przypomnienie pokazują stan z ostatniej synchronizacji należności (jak w Windykacji).',
  types: 'Rodzaje dokumentów',
  typeLabels: { FV: 'Faktury', KOR: 'Korekty', PA: 'Paragony', WZ: 'WZ', ZK: 'Zamówienia' },
  format: 'Format',
  formats: { PDF: 'PDF', XLSX: 'Excel (XLSX)', BOTH: 'PDF i Excel' },
  pdfOnly: 'Ten dokument jest dostępny tylko jako PDF.',
  totals: 'Saldo {open} · po terminie {overdue} ({count} faktur)',
  nothingOverdue: 'Klient nie ma faktur po terminie — przypomnienia nie można wysłać.',
  recipients: 'Odbiorcy',
  recipientsHint: 'Najpierw adres do dokumentów z Subiekta, potem adres główny i osoby kontaktowe.',
  sources: { ERP_DOCUMENTS: 'do dokumentów (Subiekt)', CUSTOMER: 'adres główny', CONTACT: 'osoba kontaktowa' },
  noAddresses: 'Klient nie ma adresu e-mail w NEXOPS ani w Subiekcie — wpisz adres ręcznie.',
  manual: 'Inny adres',
  manualPlaceholder: 'adres@firma.pl',
  add: 'Dodaj',
  invalidEmail: 'Niepoprawny adres e-mail',
  ccOwner: 'Kopia do opiekuna klienta ({name})',
  noOwner: 'Klient nie ma opiekuna z adresem e-mail — kopii nie będzie.',
  subject: 'Temat',
  body: 'Treść wiadomości',
  bodyHint: 'Znaczniki w nawiasach klamrowych uzupełni system: {placeholders}.',
  resetTemplate: 'Przywróć szablon',
  preview: 'Podgląd załącznika',
  previewPdf: 'Podgląd PDF',
  previewXlsx: 'Pobierz XLSX',
  previewFailed: 'Nie udało się przygotować podglądu',
  sellerIncomplete: 'Dane sprzedawcy w Ustawieniach są niepełne (NIP, adres) — uzupełnij je, zanim wyślesz dokument klientowi.',
  submit: 'Wyślij',
  cancel: 'Anuluj',
  sent: 'Wysłano do {count} odbiorców',
  failed: 'Wysyłka nie powiodła się: {error}',
  errors: {
    RECIPIENTS_INVALID: 'Sprawdź adresy odbiorców (od 1 do 10 poprawnych adresów).',
    NOTHING_OVERDUE: 'Klient nie ma faktur po terminie.',
    PDF_ONLY: 'Ten dokument jest dostępny tylko jako PDF.',
    PERIOD_INVALID: 'Sprawdź okres (początek przed końcem, nie w przyszłości).',
    PERIOD_TOO_LONG: 'Okres może mieć najwyżej 2 lata.',
    MESSAGE_EMPTY: 'Uzupełnij temat i treść wiadomości.',
    generic: 'Nie udało się wysłać dokumentu',
  },
  error: 'Nie udało się wczytać danych do wysyłki',
  log: {
    title: 'Wysłane dokumenty',
    hint: 'Zestawienia, potwierdzenia salda i przypomnienia wysłane klientowi z NEXOPS (z kopią załączników).',
    empty: 'Nie wysłano jeszcze żadnych dokumentów temu klientowi.',
    to: 'Do',
    cc: 'DW',
    by: 'Wysłał(a) {name}',
    period: 'okres {period}',
    asOf: 'stan na {date}',
    statuses: { SENT: 'wysłano', FAILED: 'błąd wysyłki' },
    error: 'Nie udało się wczytać wysłanych dokumentów',
  },
  settings: {
    section: 'Dokumenty do klienta',
    sectionHint: 'Dane sprzedawcy na zestawieniach i szablony wiadomości „Wyślij klientowi”.',
    seller: 'Dane sprzedawcy',
    fields: {
      name: 'Nazwa',
      nip: 'NIP',
      street: 'Ulica i numer',
      postalCode: 'Kod pocztowy',
      city: 'Miejscowość',
      phone: 'Telefon',
      email: 'E-mail (odpowiedzi klientów)',
      bankAccount: 'Rachunek do wpłat',
    },
    templates: 'Szablony wiadomości',
    templatesHint: 'Znaczniki: {placeholders}. Pusty temat lub treść = szablon domyślny.',
    save: 'Zapisz',
    saved: 'Zapisano ustawienia dokumentów',
    saveFailed: 'Nie udało się zapisać ustawień',
  },
};

export type CdocMessages = typeof cdocPl;

export const cdocEn: CdocMessages = {
  send: 'Send to customer',
  title: 'Send documents to the customer',
  description: 'Informational NEXOPS statements — invoices and delivery notes are sent from Subiekt (KSeF).',
  kinds: {
    DOCUMENT_LIST: 'Documents of a period',
    RECEIVABLES_STATEMENT: 'Statement of receivables',
    BALANCE_CONFIRMATION: 'Balance confirmation',
    PAYMENT_REMINDER: 'Payment reminder',
    ORDER_CONFIRMATION: 'Order confirmation',
  },
  kindHints: {
    DOCUMENT_LIST: 'Invoices, corrections and other documents of the customer in the chosen period.',
    RECEIVABLES_STATEMENT: 'Unsettled invoices with due dates, days overdue and the total.',
    BALANCE_CONFIRMATION: 'Form to confirm the balance or report a discrepancy.',
    PAYMENT_REMINDER: 'Letter listing the overdue invoices; recorded in Debt collection as an e-mail reminder.',
  },
  kind: 'Document',
  period: 'Period',
  from: 'From',
  to: 'To',
  asOf: 'Receivables as of {date}',
  asOfHint: 'The statement, confirmation and reminder show the state of the last receivables sync (as in Debt collection).',
  types: 'Document types',
  typeLabels: { FV: 'Invoices', KOR: 'Corrections', PA: 'Receipts', WZ: 'Delivery notes', ZK: 'Orders' },
  format: 'Format',
  formats: { PDF: 'PDF', XLSX: 'Excel (XLSX)', BOTH: 'PDF and Excel' },
  pdfOnly: 'This document is available as PDF only.',
  totals: 'Balance {open} · overdue {overdue} ({count} invoices)',
  nothingOverdue: 'The customer has no overdue invoices — a reminder cannot be sent.',
  recipients: 'Recipients',
  recipientsHint: 'The Subiekt address for documents first, then the main address and contact persons.',
  sources: { ERP_DOCUMENTS: 'for documents (Subiekt)', CUSTOMER: 'main address', CONTACT: 'contact person' },
  noAddresses: 'The customer has no e-mail address in NEXOPS or Subiekt — type one in.',
  manual: 'Other address',
  manualPlaceholder: 'address@company.com',
  add: 'Add',
  invalidEmail: 'Invalid e-mail address',
  ccOwner: 'Copy to the account manager ({name})',
  noOwner: 'The customer has no account manager with an e-mail — no copy will be sent.',
  subject: 'Subject',
  body: 'Message',
  bodyHint: 'Placeholders in curly braces are filled in by the system: {placeholders}.',
  resetTemplate: 'Restore template',
  preview: 'Attachment preview',
  previewPdf: 'Preview PDF',
  previewXlsx: 'Download XLSX',
  previewFailed: 'Could not prepare the preview',
  sellerIncomplete: 'The seller details in Settings are incomplete (tax id, address) — complete them before sending documents to customers.',
  submit: 'Send',
  cancel: 'Cancel',
  sent: 'Sent to {count} recipients',
  failed: 'Sending failed: {error}',
  errors: {
    RECIPIENTS_INVALID: 'Check the recipients (1 to 10 valid addresses).',
    NOTHING_OVERDUE: 'The customer has no overdue invoices.',
    PDF_ONLY: 'This document is available as PDF only.',
    PERIOD_INVALID: 'Check the period (start before end, not in the future).',
    PERIOD_TOO_LONG: 'The period can be at most 2 years.',
    MESSAGE_EMPTY: 'Fill in the subject and the message.',
    generic: 'Could not send the document',
  },
  error: 'Could not load the sending data',
  log: {
    title: 'Sent documents',
    hint: 'Statements, balance confirmations and reminders sent to the customer from NEXOPS (with a copy of the attachments).',
    empty: 'No documents have been sent to this customer yet.',
    to: 'To',
    cc: 'Cc',
    by: 'Sent by {name}',
    period: 'period {period}',
    asOf: 'as of {date}',
    statuses: { SENT: 'sent', FAILED: 'failed' },
    error: 'Could not load the sent documents',
  },
  settings: {
    section: 'Documents for customers',
    sectionHint: 'Seller details on the statements and the message templates of “Send to customer”.',
    seller: 'Seller details',
    fields: {
      name: 'Name',
      nip: 'Tax id (NIP)',
      street: 'Street and number',
      postalCode: 'Postal code',
      city: 'City',
      phone: 'Phone',
      email: 'E-mail (customer replies)',
      bankAccount: 'Bank account for payments',
    },
    templates: 'Message templates',
    templatesHint: 'Placeholders: {placeholders}. Empty subject or body = the default template.',
    save: 'Save',
    saved: 'Document settings saved',
    saveFailed: 'Could not save the settings',
  },
};
